import { ReplaceIcon } from "assets/icons";
import { GoBackHeaderTitle, If, TabLink } from "components";
import { useBoolean } from "hooks";
import { t } from "i18next";
import { BoxShadowLayout, ContentLayout } from "layouts";
import ShowAuditHistoryButton from "modules/auditLog/components/ShowAuditHistoryButton/ShowAuditHistoryButton";
import { StaffManagerPermissions, useProfile } from "modules/common";
import { ResellerFeedPreview } from "modules/recommendation";
import {
  RESELLER_INFO_ROUTES,
  ResellerMainInfo,
  useGetResellerById,
  ResellerVerificationStatusLabel,
  ResellerOrders,
  useUpdateResellerBlockStatus,
  ResellerShared,
  ResellerPayments,
  ResellerLevelControl,
  ChangeResellerDeleteStatusModal,
  ResellerCommissions,
  ResellerReferrals,
  ResellerPromoCodes
} from "modules/resellers";
import moment from "moment";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Input,
  LoaderScreen,
  Modal,
  PersonSummary,
  DeletedProfileInfo
} from "ui";

import styles from "./ResellerProfilePage.module.scss";

const ResellerProfilePage = () => {
  const navigate = useNavigate();
  const { state: resellersListSearch } = useLocation();
  const { resellerId, activeTab, usersTab } = useParams();

  const { user } = useProfile({ enabled: false });

  const { canBlock, canUnblock, canDelete, canRestore } = useMemo(() => {
    return {
      canBlock:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.BlockResellers),
      canUnblock:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.UnblockResellers),
      canDelete:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.DeleteResellers),
      canRestore:
        user.role === "Super" ||
        user.permissions?.includes(StaffManagerPermissions.RestoreResellers)
    };
  }, [user]);

  const { reseller, refetch, isLoading } = useGetResellerById(
    String(resellerId)
  );

  const [search] = useState(resellersListSearch);

  const [blockReason, setBlockReason] = useState("");

  const {
    value: isBlockModalOpen,
    setTrue: openBlockModal,
    setFalse: closeBlockModal
  } = useBoolean(false);

  const {
    value: isChangeDeleteStatusModalOpen,
    setTrue: openChangeDeleteStatusModal,
    setFalse: closeChangeDeleteStatusModal
  } = useBoolean(false);

  const updateResellerBlockStatus = useUpdateResellerBlockStatus({
    onSuccess: (_, variables) => {
      closeBlockModal();
      toast.success(
        variables.action === "block"
          ? t("User has been blocked successfully")
          : t("User has been unblocked successfully")
      );
      setBlockReason("");
      refetch();
    }
  });

  // The permanent code is the one that ties customers to the influencer; the
  // referrals tab falls back to it for links made before codes were recorded.
  const permanentPromoCode = useMemo(
    () =>
      reseller?.promoCodes?.find((promoCode) => promoCode.isPermanent)?.code,
    [reseller?.promoCodes]
  );

  const tabCounters: Record<string, number | undefined> = {
    Orders: reseller?.totalNumberOfOrders,
    Commissions: reseller?.totalNumberOfCommissions,
    Referrals: reseller?.totalNumberOfReferrals,
    Shared: reseller?.totalNumberOfProductSharings,
    Transactions: reseller?.totalNumberOfTransactions
  };

  const daysLeftToRestore = Math.ceil(
    moment(reseller?.accountRestorationDeadline).diff(moment(), "days", true)
  );

  const updateBlockStatus = () => {
    if (reseller?.isBlocked) {
      updateResellerBlockStatus.mutate({
        resellerId: String(resellerId),
        action: "unblock",
        reason: blockReason
      });
    } else {
      openBlockModal();
    }
  };

  return (
    <>
      <ContentLayout
        title={
          <div className={styles.pageHeader}>
            <GoBackHeaderTitle
              title={t("Reseller Profile")}
              onGoBack={() =>
                navigate({ pathname: `/users/${usersTab}`, search })
              }
            />

            <If condition={reseller}>
              <div className={styles.pageHeader_buttons}>
                {reseller?.isDeleted ? (
                  <>
                    <DeletedProfileInfo
                      deletedAt={reseller.deletedAt}
                      deletedBy={reseller.deletedBy}
                      accountRestorationDeadline={
                        reseller.accountRestorationDeadline
                      }
                    />
                    {canRestore && daysLeftToRestore > 0 && (
                      <Button
                        icon={
                          <ReplaceIcon width={24} height={24} fill="#FFFFFF" />
                        }
                        onClick={openChangeDeleteStatusModal}
                      >
                        {t("Restore")}
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    {!reseller?.isBlocked && canBlock ? (
                      <Button variant="additional" onClick={updateBlockStatus}>
                        {t("Block")}
                      </Button>
                    ) : reseller?.isBlocked && canUnblock ? (
                      <Button variant="additional" onClick={updateBlockStatus}>
                        {t("Unblock")}
                      </Button>
                    ) : null}

                    {canDelete && (
                      <Button
                        variant="delete"
                        onClick={openChangeDeleteStatusModal}
                      >
                        {t("Delete")}
                      </Button>
                    )}

                    <ShowAuditHistoryButton
                      entityType="Reseller"
                      entityId={resellerId}
                    />
                  </>
                )}
              </div>
            </If>
          </div>
        }
      >
        <BoxShadowLayout className={styles.pageContent}>
          <PersonSummary
            personImage={reseller?.image}
            isLoading={isLoading}
            personId={reseller?.id}
            personFullName={
              reseller ? `${reseller.firstname} ${reseller.lastname}` : ""
            }
            topElement={
              reseller ? (
                <ResellerLevelControl
                  currentLevel={reseller.level}
                  resellerId={reseller?.id}
                  isDisabled={reseller?.isDeleted}
                />
              ) : null
            }
            nameMarker={
              <>
                {reseller?.isBlocked && <span>🚫</span>}
                <ResellerVerificationStatusLabel
                  status={reseller?.verificationStatus}
                />
              </>
            }
            statistic={[
              {
                label: t("In Progress"),
                value: reseller?.numberOfInProgressOrders
              },
              { label: t("Canceled"), value: reseller?.numberOfCanceledOrders },
              { label: t("Failed"), value: reseller?.numberOfFailedOrders },
              {
                label: t("Delivered"),
                value: reseller?.numberOfDeliveredOrders
              },
              { label: t("Returned"), value: reseller?.numberOfReturnedOrders }
            ]}
          />

          <ResellerPromoCodes promoCodes={reseller?.promoCodes} />

          <nav>
            {RESELLER_INFO_ROUTES?.map((route) => (
              <TabLink
                key={route.label}
                pathName={`/users/${usersTab}/${resellerId}/${route.path}`}
                label={t(route.label)}
                counterValue={tabCounters[route.label]}
                forceActive={activeTab === route.path}
              />
            ))}
          </nav>

          <If condition={activeTab === "mainInfo"}>
            <ResellerMainInfo reseller={reseller} />
          </If>

          <If condition={activeTab === "orders"}>
            <ResellerOrders />
          </If>

          <If condition={activeTab === "commissions"}>
            <ResellerCommissions />
          </If>

          <If condition={activeTab === "referrals"}>
            <ResellerReferrals permanentCode={permanentPromoCode} />
          </If>

          <If condition={activeTab === "shared"}>
            <ResellerShared />
          </If>

          <If condition={activeTab === "transactions"}>
            <ResellerPayments />
          </If>

          <If condition={activeTab === "recommendations"}>
            <ResellerFeedPreview resellerId={String(resellerId)} />
          </If>
        </BoxShadowLayout>
      </ContentLayout>

      <ChangeResellerDeleteStatusModal
        isOpen={isChangeDeleteStatusModalOpen}
        resellerId={String(resellerId)}
        action={reseller?.isDeleted ? "Restore" : "Delete"}
        onClose={closeChangeDeleteStatusModal}
      />

      <Modal
        isOpen={isBlockModalOpen}
        title="Block Reseller"
        isLoading={updateResellerBlockStatus.isLoading}
        okText="Block"
        isOkDisabled={blockReason.length < 1}
        onOk={() =>
          updateResellerBlockStatus.mutate({
            resellerId: String(resellerId),
            reason: blockReason,
            action: "block"
          })
        }
        onClose={closeBlockModal}
      >
        <Input
          asTag={"textarea"}
          placeholder="Enter a reason"
          value={blockReason}
          onChange={(e) => setBlockReason(e.target.value)}
        />
      </Modal>

      {updateResellerBlockStatus.isLoading && <LoaderScreen />}
    </>
  );
};

export default ResellerProfilePage;
